/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Skeleton } from '@radix-ui/themes'
import { useNavigate } from '@tanstack/react-router'
import {
  IconChevronDown,
  IconChevronUp,
  IconSortAscendingNumbers,
  IconSortDescendingNumbers,
} from '@tabler/icons-react'
import {
  useGetClasses,
  useGetListClassTutors,
  useGetListClassYears,
} from '@/services/classService'
import { ClassStatus } from '@/types/class.type'
import { ModuleItemType } from '@/types/module'
import { useClassStore } from '@/stores/useClassStore'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import TablePaginate from '@/components/ui/table-paginate'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import ModuleCard from './components/module-card'
import { AWS_BUCKET_NAME, AWS_REGION } from './constants'

const Modules = () => {
  const navigate = useNavigate()
  const { items: modules, setSelectedModule } = useModulesStore()

  const [tab, setTab] = useState<string>('0')
  const [page, setPage] = useState(1)

  const { setSelectedClass } = useClassStore()

  const [search, onSearchChange] = useState<string>('')
  const debouncedSearch = useDebounce(search)
  const [isYearFilterOpen, setIsYearFilterOpen] = useState(false)
  const [isTutorFilterOpen, setIsTutorFilterOpen] = useState(false)
  const [selectedYears, setSelectedYears] = useState<number[]>([])
  const [selectedTutors, setSelectedTutors] = useState<string[]>([])
  const [yearSortOrder, setYearSortOrder] = useState<
    'default' | 'asc' | 'desc'
  >('default')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [classStatus, setClassStatus] = useState<ClassStatus | null>(null)

  const moduleId = tab === '0' ? undefined : Number(tab)
  const availableYears = useGetListClassYears(moduleId)?.classYears || []
  const availableTutors = useGetListClassTutors(moduleId)?.classTutors || []

  const getClassStatusDisplay = (status: ClassStatus | null) => {
    if (status === ClassStatus.active) return 'Active'
    if (status === ClassStatus.deactivated) return 'Deactivated'
    return 'All'
  }

  const { classes, isLoadingClasses, meta } = useGetClasses({
    module_id: tab,
    page,
    size: 10,
    is_active:
      classStatus === ClassStatus.active
        ? true
        : classStatus === ClassStatus.deactivated
          ? false
          : undefined,
    query: debouncedSearch.trim() || undefined,
    years: selectedYears,
    tutors: selectedTutors,
    year_asc:
      yearSortOrder === 'asc'
        ? true
        : yearSortOrder === 'desc'
          ? false
          : undefined,
  })

  useEffect(() => {
    if (modules?.length) {
      setSelectedModule(modules?.[0])
    }
  }, [modules])

  useEffect(() => {
    setSelectedYears([])
    setSelectedTutors([])
    setClassStatus(null)
    setYearSortOrder('default')
  }, [tab])

  return (
    <Main className='pt-4'>
      <PageTitle title='Class Management' />

      <div className='mb-4 py-1'>
        <ul className='mb-4 flex w-full gap-1 overflow-auto pb-1'>
          <li>
            <button
              onClick={() => {
                setSelectedModule({
                  id: 0,
                  name: '',
                  description: '',
                  total_classes: 0,
                  total_practice_test: 0,
                  total_mock_exam: 0,
                })
                setTab('0')
                setPage(1)
              }}
              className={cn(
                'group flex w-full items-center gap-3 rounded-[8px] border-[1px] border-[#EAECF0] bg-[#fff] px-4 py-2.5 text-sm capitalize transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0057B8]/30',
                tab === '0'
                  ? 'rounded-[8px] border-[1px] border-[#0069E0] bg-[#EBF4FF] text-[#0057B8]'
                  : 'text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#003A80]'
              )}
            >
              <span className='truncate text-base'>All</span>
            </button>
          </li>
          {modules?.map((item: ModuleItemType) => {
            const id = String(item.id)
            const isActive = tab === id
            return (
              <li key={id}>
                <button
                  onClick={() => {
                    setSelectedModule(item)
                    setTab(item.id.toString())
                    setPage(1)
                  }}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-[8px] border-[1px] border-[#EAECF0] bg-[#fff] px-4 py-2.5 text-sm capitalize transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0057B8]/30',
                    isActive
                      ? 'rounded-[8px] border-[1px] border-[#0069E0] bg-[#EBF4FF] text-[#0057B8]'
                      : 'text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#003A80]'
                  )}
                  aria-label={item.name}
                >
                  <span className='truncate text-base'>{item.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
      <div className='mb-3 flex justify-between'>
        <SearchBox
          containerClass='w-[350px]'
          placeHolder='Search by Class Name'
          onValueChange={onSearchChange}
        />
        <div className='flex justify-end gap-2'>
          <DropdownMenu onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                className='inline-flex h-8 w-[180px] items-center justify-between gap-1 px-3 lg:px-4'
              >
                <span className='truncate'>
                  Status:{' '}
                  {classStatus ? getClassStatusDisplay(classStatus) : 'All'}
                </span>
                {isDropdownOpen ? (
                  <IconChevronUp className='size-4 flex-shrink-0' />
                ) : (
                  <IconChevronDown className='size-4 flex-shrink-0' />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                key='all'
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm',
                  classStatus === null
                    ? 'bg-gray-100 text-[#002652]'
                    : 'text-[#475467] hover:bg-gray-100'
                )}
                onSelect={() => setClassStatus(null)}
              >
                All
              </DropdownMenuItem>
              {Object.values(ClassStatus).map((item: string) => (
                <DropdownMenuItem
                  key={item}
                  className={cn(
                    'cursor-pointer px-3 py-2 text-sm',
                    classStatus === item
                      ? 'bg-gray-100 text-[#002652]'
                      : 'text-[#475467] hover:bg-gray-100'
                  )}
                  onSelect={() => setClassStatus(item as ClassStatus)}
                >
                  {getClassStatusDisplay(item as ClassStatus)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Popover open={isTutorFilterOpen} onOpenChange={setIsTutorFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className='inline-flex h-8 w-[200px] items-center justify-between gap-1 px-3 lg:px-4'
              >
                <span className='truncate'>
                  Tutor:{' '}
                  {selectedTutors.length === 0
                    ? 'All'
                    : selectedTutors.length === 1
                      ? availableTutors.find(
                          (t) => t.username === selectedTutors[0]
                        )?.fullname || selectedTutors[0]
                      : `${availableTutors.find((t) => t.username === selectedTutors[0])?.fullname || selectedTutors[0]}, +${selectedTutors.length - 1}`}
                </span>
                {isTutorFilterOpen ? (
                  <IconChevronUp className='size-4 shrink-0' />
                ) : (
                  <IconChevronDown className='size-4 shrink-0' />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[240px] p-0' align='end'>
              <div className='max-h-[300px] overflow-y-auto p-2'>
                {!availableTutors || availableTutors.length === 0 ? (
                  <div className='p-4 text-center text-sm text-gray-500'>
                    No tutors available
                  </div>
                ) : (
                  availableTutors.map((tutor) => (
                    <div
                      key={tutor.id}
                      className='flex items-center gap-2 rounded px-2 py-2 hover:bg-gray-100'
                    >
                      <Checkbox
                        id={`tutor-${tutor.id}`}
                        checked={selectedTutors.includes(tutor.fullname)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTutors([
                              ...selectedTutors,
                              tutor.fullname,
                            ])
                          } else {
                            setSelectedTutors(
                              selectedTutors.filter((t) => t !== tutor.fullname)
                            )
                          }
                        }}
                      />
                      <label
                        htmlFor={`tutor-${tutor.id}`}
                        className='line-clamp-2 flex-1 cursor-pointer text-sm'
                      >
                        {tutor.fullname}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={isYearFilterOpen} onOpenChange={setIsYearFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className='inline-flex h-8 w-[200px] items-center justify-between gap-1 px-3 lg:px-4'
              >
                <span className='truncate'>
                  Year:{' '}
                  {yearSortOrder === 'asc'
                    ? 'Ascending'
                    : yearSortOrder === 'desc'
                      ? 'Descending'
                      : selectedYears.length === 0
                        ? 'All'
                        : selectedYears.length === 1
                          ? selectedYears[0]
                          : `${selectedYears[0]}, ${selectedYears[1]}...`}
                </span>
                {isYearFilterOpen ? (
                  <IconChevronUp className='size-4 shrink-0' />
                ) : (
                  <IconChevronDown className='size-4 shrink-0' />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[240px] p-0' align='end'>
              <div className='border-b p-3'>
                <button
                  onClick={() => {
                    setYearSortOrder((prev) => {
                      const newSort =
                        prev === 'default'
                          ? 'asc'
                          : prev === 'asc'
                            ? 'desc'
                            : 'default'
                      // Clear selected years when changing to asc or desc
                      if (newSort !== 'default') {
                        setSelectedYears([])
                      }
                      return newSort
                    })
                  }}
                  className='flex w-full items-center justify-between text-sm'
                >
                  <span>
                    Sort:{' '}
                    {yearSortOrder === 'default'
                      ? 'Default'
                      : yearSortOrder === 'asc'
                        ? 'Ascending'
                        : 'Descending'}
                  </span>
                  {yearSortOrder === 'asc' ? (
                    <IconSortAscendingNumbers className='size-5' />
                  ) : yearSortOrder === 'desc' ? (
                    <IconSortDescendingNumbers className='size-5' />
                  ) : (
                    <IconSortDescendingNumbers className='size-5 opacity-40' />
                  )}
                </button>
              </div>
              <div className='max-h-[300px] overflow-y-auto p-2'>
                {!availableYears || availableYears.length === 0 ? (
                  <div className='p-4 text-center text-sm text-gray-500'>
                    No years available
                  </div>
                ) : (
                  availableYears
                    .sort((a, b) => {
                      if (yearSortOrder === 'asc') return a - b
                      if (yearSortOrder === 'desc') return b - a
                      return 0
                    })
                    .map((year) => (
                      <div
                        key={year}
                        className='flex items-center gap-2 rounded px-2 py-2 hover:bg-gray-100'
                      >
                        <Checkbox
                          id={`year-${year}`}
                          checked={selectedYears.includes(year)}
                          onCheckedChange={(checked) => {
                            // Reset sort to default when selecting years
                            if (yearSortOrder !== 'default') {
                              setYearSortOrder('default')
                            }
                            if (checked) {
                              setSelectedYears([...selectedYears, year])
                            } else {
                              setSelectedYears(
                                selectedYears.filter((y) => y !== year)
                              )
                            }
                          }}
                        />
                        <label
                          htmlFor={`year-${year}`}
                          className='flex-1 cursor-pointer text-sm'
                        >
                          {year}
                        </label>
                      </div>
                    ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className={cn('grid min-h-[320px] grid-cols-5 gap-3')}>
        {isLoadingClasses ? (
          <>
            {[...Array(5)].map(() => (
              <Skeleton
                height='295px'
                className='max-w-[293px] rounded-[16px]'
              />
            ))}
          </>
        ) : (
          <>
            {classes?.length === 0 && (
              <div className='col-span-5 h-24 text-center'>No data found.</div>
            )}
            {classes?.map((item) => (
              <ModuleCard
                key={item.class_id}
                id={item.class_id.toString()}
                title={item.class_name}
                imageUrl={
                  item.image_link
                    ? `https://${AWS_BUCKET_NAME}.s3.${AWS_REGION}.amazonaws.com/${item.image_link}`
                    : undefined
                }
                tutors={item.tutors_name}
                tutorsDetail={item.tutors.map((t) => ({
                  ...t,
                  image: t.image
                    ? `https://${AWS_BUCKET_NAME}.s3.${AWS_REGION}.amazonaws.com/${t.image}`
                    : null,
                }))}
                moduleName={item.module_name}
                students={item.total_students}
                tests={item.total_tests}
                status={item.class_status}
                as='button'
                onClick={() => {
                  setSelectedClass(item)
                  navigate({
                    to: '/class/$classId/$moduleId',
                    params: {
                      classId: item.class_id.toString(),
                      moduleId: item?.module_id
                        ? item.module_id.toString()
                        : '',
                    },
                  })
                }}
              />
            ))}
          </>
        )}
      </div>
      {meta && meta?.total_pages > 1 && (
        <div className='flex justify-end p-4 pr-0'>
          <TablePaginate
            itemPerPage={meta.items_per_page}
            totalItems={meta.total_items}
            page={page}
            totalPages={meta.total_pages}
            onPageChange={setPage}
            readOnlyPageDisplay={false}
            hideOnSinglePage={false}
          />
        </div>
      )}
    </Main>
  )
}

export default Modules
